import { useState, useEffect, useRef, useCallback } from 'react';
import { formatDate, daysBetween, hasVisibleContent, hasTextContent, stripHtml, stripImgTags, uid, timeAgo, getContrastColor } from '../utils';
import { DEFAULT_COLORS } from '../constants';
import { Storage } from '../storage';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import ReportModal from '../components/ReportModal';
import TimelineCanvas from '../components/TimelineCanvas';
import NotificationBell from '../components/NotificationBell';
import MobileMenu from '../components/MobileMenu';

// ─── YouTube embed component ──────────────────────────────────────────────────

const toYtEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u    = new URL(url.trim());
    const host = u.hostname.replace('www.', '').replace('m.', '');
    let id = null;
    if (host === 'youtu.be') {
      id = u.pathname.slice(1).split('/')[0];
    } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/')) {
        id = u.pathname.split('/')[2];
      } else {
        id = u.searchParams.get('v');
      }
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) return `https://www.youtube-nocookie.com/embed/${id}`;
  } catch {}
  return null;
};

const YouTubeEmbed = ({ embedUrl, title }) => {
  const [hasError, setHasError] = useState(false);
  const src = toYtEmbedUrl(embedUrl);
  if (!src || hasError) return null;
  return (
    <div style={{
      position:   'relative',
      width:      '100%',
      paddingTop: '56.25%',
      borderRadius: 8,
      overflow:   'hidden',
      marginTop:  12,
      background: 'var(--bg2)',
    }}>
      <iframe
        src={src}
        title={title || 'Event video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onError={() => setHasError(true)}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

// ─── TIMELINE VIEWER PAGE ─────────────────────────────────────────────────────

const TimelineViewer = ({ timeline, onBack, backLabel = 'My Dashboard', onEdit, onFork, currentUser, onViewProfile, onViewTimeline, onAuthRequired, theme, onThemeToggle, onNotificationClick }) => {

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [selectedEvent,  setSelectedEvent]  = useState(null);
  const [shareCopied,       setShareCopied]       = useState(false);
  const [carouselExporting, setCarouselExporting] = useState(false);
  const [social,         setSocial]         = useState(null);
  const [showComments,   setShowComments]   = useState(false);
  const [commentText,    setCommentText]    = useState('');
  const [replyingTo,     setReplyingTo]     = useState(null);
  const [replyText,      setReplyText]      = useState('');
  const [reportTarget,   setReportTarget]   = useState(null);
  const [reportedIds,    setReportedIds]    = useState(new Set());
  const [collaborators,  setCollaborators]  = useState(timeline.collaborators || []);
  const [toast,               setToast]               = useState(null); // { message, type }
  const [blockConfirm,        setBlockConfirm]        = useState(null); // { userId, userName }
  const [blockedIds,          setBlockedIds]          = useState(new Set());
  const [showReactionPicker,  setShowReactionPicker]  = useState(false);
  const reactionPickerTimer                           = useRef(null);
  const [forkLoading,         setForkLoading]         = useState(false);

  // Bookmark state
  const [isBookmarked,    setIsBookmarked]    = useState(!!timeline.userHasBookmarked);
  const [bookmarkCount,   setBookmarkCount]   = useState(timeline.bookmarkCount ?? 0);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Search highlight state — populated from navigation context (_searchQuery / _matchedEventIds)
  const [searchHighlight,     setSearchHighlight]     = useState(timeline._searchQuery || '');
  const [highlightedEventIds, setHighlightedEventIds] = useState(
    () => new Set(timeline._matchedEventIds || [])
  );

  // Recommendations state
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoaded,      setRecsLoaded]      = useState(false);

  // @mention autocomplete state
  const [mentionQuery,   setMentionQuery]   = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionActive,  setMentionActive]  = useState(false); // 'comment' | 'reply' | null
  const [mentionIndex,   setMentionIndex]   = useState(0);
  const commentTextareaRef = useRef(null);
  const replyTextareaRef   = useRef(null);
  const mentionDebounce    = useRef(null);

  // Comment like state: { [commentId]: { count, liked } }
  const [commentLikes, setCommentLikes] = useState({});
  // Liker tooltip data: { [commentId]: likers[] | null }
  const [likerTips,   setLikerTips]    = useState({});

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Inject Open Graph / Twitter Card meta tags while this timeline is mounted
  useEffect(() => {
    if (!timeline) return;

    const strip = (html) => (html || '').replace(/<[^>]+>/g, '').trim();
    const desc  = strip(timeline.description).slice(0, 160) || 'An interactive timeline built with Chronos.';

    const firstPhoto = (timeline.events || []).find(ev => ev.photo)?.photo ?? null;
    const image      = firstPhoto || timeline.bgImage || null;
    const canonical  = `${window.location.origin}/timeline/${timeline.id}`;

    const tags = [
      { property: 'og:type',        content: 'article' },
      { property: 'og:site_name',   content: 'Chronos' },
      { property: 'og:title',       content: timeline.title },
      { property: 'og:description', content: desc },
      { property: 'og:url',         content: canonical },
      { name:     'twitter:card',   content: image ? 'summary_large_image' : 'summary' },
      { name:     'twitter:title',  content: timeline.title },
      { name:     'twitter:description', content: desc },
      ...(image ? [
        { property: 'og:image',      content: image },
        { name:     'twitter:image', content: image },
      ] : []),
    ];

    const injected = [];
    for (const attrs of tags) {
      const el = document.createElement('meta');
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      el.setAttribute('data-chronos-og', 'true');
      document.head.appendChild(el);
      injected.push(el);
    }

    const prevTitle = document.title;
    document.title  = `${timeline.title} — Chronos`;

    return () => {
      injected.forEach(el => el.remove());
      document.title = prevTitle;
    };
  }, [timeline]);

  // Load recommendations lazily after the viewer has mounted
  useEffect(() => {
    Storage.getRecommendations(timeline.id, 6)
      .then(({ recommendations: recs }) => setRecommendations(recs || []))
      .catch(() => {})
      .finally(() => setRecsLoaded(true));
  }, [timeline.id]);

  useEffect(() => {
    if (!currentUser) return;
    Storage.getSocialForTimeline(timeline.id)
      .then(data => {
        setSocial(data);
        // Seed comment like state from server response
        const cl = {};
        for (const c of (data.comments || [])) {
          cl[c.id] = { count: c.likeCount || 0, liked: !!c.userHasLiked };
          for (const r of (c.replies || [])) {
            cl[r.id] = { count: r.likeCount || 0, liked: !!r.userHasLiked };
          }
        }
        setCommentLikes(cl);
      })
      .catch(() => {});
  }, [timeline.id, currentUser]);

  useEffect(() => {
    if (!currentUser || !timeline.id) return;
    if (timeline.collaborators?.length) return;
    Storage.getCollaborators(timeline.id)
      .then(({ collaborators: c }) => setCollaborators(c))
      .catch(() => {});
  }, [timeline.id, currentUser]);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCarouselExport = async () => {
    if (!currentUser) { onAuthRequired?.('carousel'); return; }
    setCarouselExporting(true);
    try {
      await Storage.exportCarousel(timeline.id, timeline.title);
    } catch (err) {
      showToast(err.message || 'Carousel export failed', 'error');
    } finally {
      setCarouselExporting(false);
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/timeline/${timeline.id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleBookmark = async () => {
    if (!currentUser) { onAuthRequired?.('bookmark'); return; }
    if (bookmarkLoading) return;
    const wasBookmarked = isBookmarked;
    setIsBookmarked(!wasBookmarked);
    setBookmarkCount(c => wasBookmarked ? Math.max(0, c - 1) : c + 1);
    setBookmarkLoading(true);
    try {
      if (wasBookmarked) {
        const { bookmarkCount: n } = await Storage.removeBookmark(timeline.id);
        setBookmarkCount(n);
      } else {
        const { bookmarkCount: n } = await Storage.addBookmark(timeline.id);
        setBookmarkCount(n);
      }
    } catch (err) {
      setIsBookmarked(wasBookmarked);
      setBookmarkCount(c => wasBookmarked ? c + 1 : Math.max(0, c - 1));
      showToast(err?.message || 'Could not update bookmark', 'error');
    } finally {
      setBookmarkLoading(false);
    }
  };

  const pushSocial = async (updated) => {
    setSocial(updated);
    await Storage.updateSocial(timeline.id, updated);
  };

  const REACTIONS = [
    { type: 'heart',      emoji: '❤️', label: 'Heart' },
    { type: 'fire',       emoji: '🔥', label: 'Fire' },
    { type: 'mind_blown', emoji: '🤯', label: 'Mind Blown' },
    { type: 'sad',        emoji: '😢', label: 'Sad' },
  ];

  const handleReact = (type) => {
    if (!currentUser) { onAuthRequired?.('react'); return; }
    if (!social) return;
    const likes    = social.likes || [];
    const existing = likes.find(l => l.userId === currentUser.id);
    let newLikes;
    if (!existing) {
      newLikes = [...likes, { userId: currentUser.id, userName: currentUser.name, userAvatar: currentUser.avatar || null, reactionType: type }];
    } else if ((existing.reactionType || 'heart') === type) {
      newLikes = likes.filter(l => l.userId !== currentUser.id);
    } else {
      newLikes = likes.map(l => l.userId === currentUser.id ? { ...l, reactionType: type } : l);
    }
    pushSocial({ ...social, likes: newLikes });
    setShowReactionPicker(false);
  };

  const handleFork = async () => {
    if (!currentUser) { onAuthRequired?.('fork'); return; }
    if (forkLoading) return;
    setForkLoading(true);
    try {
      const { timeline: forked } = await Storage.forkTimeline(timeline.id);
      showToast('Timeline forked! Opening in editor…', 'success');
      setTimeout(() => onFork?.(forked), 800);
    } catch (err) {
      showToast(err?.message || 'Could not fork timeline', 'error');
    } finally {
      setForkLoading(false);
    }
  };

  const handleComment = () => {
    if (!currentUser) { onAuthRequired?.('comment'); return; }
    if (!commentText.trim() || !social) return;
    const c = {
      id: uid(), userId: currentUser.id, userName: currentUser.name,
      userAvatar: currentUser.avatar || null,
      text: commentText.trim(), timestamp: Date.now(), replies: [],
    };
    pushSocial({ ...social, comments: [...(social.comments || []), c] });
    setCommentText('');
  };

  const handleReply = (commentId) => {
    if (!currentUser) { onAuthRequired?.('comment'); return; }
    if (!replyText.trim() || !social) return;
    const r = {
      id: uid(), userId: currentUser.id, userName: currentUser.name,
      userAvatar: currentUser.avatar || null,
      text: replyText.trim(), timestamp: Date.now(),
    };
    const comments = (social.comments || []).map(c =>
      c.id === commentId ? { ...c, replies: [...(c.replies || []), r] } : c
    );
    pushSocial({ ...social, comments });
    setReplyText('');
    setReplyingTo(null);
  };

  const handleDeleteComment = (commentId) => {
    if (!social) return;
    pushSocial({ ...social, comments: (social.comments || []).filter(c => c.id !== commentId) });
  };

  const handleDeleteReply = (commentId, replyId) => {
    if (!social) return;
    const comments = (social.comments || []).map(c =>
      c.id === commentId ? { ...c, replies: (c.replies || []).filter(r => r.id !== replyId) } : c
    );
    pushSocial({ ...social, comments });
  };

  // ── Comment / Reply likes ───────────────────────────────────────────────────

  const handleCommentLike = async (id, isReply = false) => {
    if (!currentUser) { onAuthRequired?.('like'); return; }
    const cur = commentLikes[id] || { count: 0, liked: false };
    // Optimistic update
    setCommentLikes(prev => ({ ...prev, [id]: { count: cur.liked ? cur.count - 1 : cur.count + 1, liked: !cur.liked } }));
    try {
      const fn = isReply
        ? (cur.liked ? Storage.unlikeReply : Storage.likeReply)
        : (cur.liked ? Storage.unlikeComment : Storage.likeComment);
      const newCount = await fn(id);
      setCommentLikes(prev => ({ ...prev, [id]: { count: newCount, liked: !cur.liked } }));
    } catch (err) {
      setCommentLikes(prev => ({ ...prev, [id]: cur }));
      showToast(err?.message || 'Could not update like', 'error');
    }
  };

  const handleLikerHover = async (id) => {
    if (likerTips[id] !== undefined) return;
    setLikerTips(prev => ({ ...prev, [id]: null })); // null = loading
    try {
      const likers = await Storage.getCommentLikers(id);
      setLikerTips(prev => ({ ...prev, [id]: likers }));
    } catch {
      setLikerTips(prev => ({ ...prev, [id]: [] }));
    }
  };

  // ── @mention autocomplete ───────────────────────────────────────────────────

  const handleMentionInput = useCallback((text, fieldType) => {
    const at = text.lastIndexOf('@');
    if (at === -1) { setMentionActive(null); return; }
    const afterAt = text.slice(at + 1);
    if (/\s/.test(afterAt) && afterAt.length > 0) { setMentionActive(null); return; }
    if (afterAt.length === 0) { setMentionActive(null); return; }
    setMentionQuery(afterAt);
    setMentionActive(fieldType);
    setMentionIndex(0);
    clearTimeout(mentionDebounce.current);
    mentionDebounce.current = setTimeout(async () => {
      try {
        const users = await Storage.searchUsers(afterAt);
        setMentionResults(users);
      } catch { setMentionResults([]); }
    }, 200);
  }, []);

  const insertMention = useCallback((user, fieldType) => {
    const isComment = fieldType === 'comment';
    const text      = isComment ? commentText : replyText;
    const setText   = isComment ? setCommentText : setReplyText;
    const at = text.lastIndexOf('@');
    const newText = text.slice(0, at) + `@${user.name} `;
    setText(newText);
    setMentionActive(null);
    setMentionResults([]);
    if (isComment) commentTextareaRef.current?.focus();
    else           replyTextareaRef.current?.focus();
  }, [commentText, replyText]);

  const handleCommentKeyDown = (e) => {
    if (mentionActive === 'comment' && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionResults[mentionIndex], 'comment'); return; }
      if (e.key === 'Escape')    { setMentionActive(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); }
  };

  const handleReplyKeyDown = (e) => {
    if (mentionActive === 'reply' && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionResults[mentionIndex], 'reply'); return; }
      if (e.key === 'Escape')    { setMentionActive(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); }
  };

  // ── Blocking ────────────────────────────────────────────────────────────────

  const confirmBlock = (userId, userName) => setBlockConfirm({ userId, userName });

  const executeBlock = async () => {
    if (!blockConfirm) return;
    try {
      await Storage.blockUser(blockConfirm.userId);
      setBlockedIds(prev => new Set([...prev, blockConfirm.userId]));
      showToast(`${blockConfirm.userName} has been blocked.`, 'success');
    } catch {
      showToast('Could not block user.', 'error');
    } finally {
      setBlockConfirm(null);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const renderAvatar = (u, size) => {
    if (u?.avatar) return <img src={u.avatar} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
    const initials = ((u?.name) || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.floor(size * 0.38), fontWeight: 700, color: 'var(--on-accent)', userSelect: 'none',
      }}>
        {initials}
      </div>
    );
  };

  // Render comment text with @mentions highlighted as links
  const renderMentionText = (text) => {
    if (!text) return null;
    const parts = text.split(/(@\S+)/g);
    return parts.map((part, i) =>
      part.startsWith('@') ? (
        <span key={i} style={{ color: 'var(--accent)', fontWeight: 600 }}>{part}</span>
      ) : part
    );
  };

  // Render comment like button (shared for comments and replies)
  const renderLikeButton = (id, isReply = false) => {
    const state = commentLikes[id] || { count: 0, liked: false };
    const isAuth = !!currentUser;
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => isAuth ? handleCommentLike(id, isReply) : onAuthRequired?.('like')}
          aria-label={state.liked ? 'Unlike' : 'Like'}
          className={state.liked ? 'comment-like-btn liked' : 'comment-like-btn'}
          style={{
            background: 'none', border: 'none', cursor: isAuth ? 'pointer' : 'default',
            display: 'inline-flex', alignItems: 'center', gap: 3,
            color: state.liked ? '#e05c5c' : 'var(--text3)',
            opacity: isAuth ? 1 : 0.6,
            fontSize: 12, padding: '2px 4px', borderRadius: 4,
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => {
            if (isAuth && !state.liked) e.currentTarget.style.color = '#e05c5c';
            if (state.count > 0) handleLikerHover(id);
          }}
          onMouseLeave={e => { if (!state.liked) e.currentTarget.style.color = 'var(--text3)'; }}
        >
          <Icon name="heart" size={12} fill={state.liked ? 'currentColor' : 'none'} color="currentColor" />
          {state.count > 0 && <span>{state.count}</span>}
        </button>
        {/* Liker tooltip */}
        {state.count > 0 && likerTips[id] && likerTips[id].length > 0 && (
          <div className="liker-tooltip" style={{
            position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, zIndex: 60,
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius)', padding: '6px 10px',
            minWidth: 120, maxWidth: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            pointerEvents: 'none', fontSize: 12,
          }}>
            {(() => {
              const shown = likerTips[id].slice(0, 5);
              const rest  = likerTips[id].length - shown.length;
              const names = shown.map(l => l.userName).join(', ');
              return <span style={{ color: 'var(--text2)' }}>
                Liked by {names}{rest > 0 ? ` and ${rest} other${rest !== 1 ? 's' : ''}` : ''}
              </span>;
            })()}
          </div>
        )}
      </div>
    );
  };

  const colors   = timeline.colors || DEFAULT_COLORS;
  const sorted   = [...timeline.events].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const minDate  = sorted.length ? sorted[0].startDate : null;
  const maxDate  = sorted.length ? sorted[sorted.length - 1].endDate : null;
  const minShowEra = sorted.length ? sorted[0].startEra !== 'NONE' : true;
  const maxShowEra = sorted.length ? sorted[sorted.length - 1].endEra !== 'NONE' : true;
  const headerBg = timeline.bgImage
    ? { backgroundImage: `url(${timeline.bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: colors.tertiary };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, background: 'var(--surface)', border: '1px solid var(--border2)',
          borderRadius: 'var(--radius)', padding: '10px 20px', fontSize: 13,
          color: toast.type === 'error' ? 'var(--danger)' : toast.type === 'success' ? 'var(--success)' : 'var(--text)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)', pointerEvents: 'none',
        }}>
          {toast.message}
        </div>
      )}

      {/* ── Header ── */}
      <header
        className="timeline-header viewer-banner"
        style={{
          ...headerBg,
          borderBottom: `1px solid ${colors.primary}40`,
          padding: '20px 32px 32px',
          position: 'relative',
          display: 'flex', flexDirection: 'column', gap: 0,
        }}
      >
        {timeline.bgImage && (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: `${colors.tertiary}cc` }} />
        )}
        <div style={{
          position: 'relative', zIndex: 1,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 24,
        }}>
          <button
            className="btn-ghost btn-sm"
            onClick={onBack}
            style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.primary, borderColor: `${colors.primary}50` }}
          >
            <Icon name="back" size={16} /> {backLabel}
          </button>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Desktop-only action buttons */}
            {!isMobile && (<>
              <button
                onClick={handleBookmark}
                aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
                disabled={bookmarkLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: isBookmarked ? `${colors.primary}25` : `${colors.primary}12`,
                  color: colors.primary,
                  border: `1px solid ${isBookmarked ? `${colors.primary}60` : `${colors.primary}30`}`,
                  borderRadius: 'var(--radius)', padding: '7px 14px',
                  cursor: bookmarkLoading ? 'default' : 'pointer',
                  fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
                  opacity: bookmarkLoading ? 0.6 : 1,
                }}
              >
                <Icon name="bookmark" size={14} fill={isBookmarked ? 'currentColor' : 'none'} color="currentColor" />
                {bookmarkCount > 0 ? bookmarkCount : (isBookmarked ? 'Saved' : 'Save')}
              </button>

              <button
                onClick={handleShare}
                aria-label="Copy link to this timeline"
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: shareCopied ? 'rgba(91,184,91,0.18)' : `${colors.primary}22`,
                  color: shareCopied ? '#5cb85c' : colors.primary,
                  border: `1px solid ${shareCopied ? 'rgba(91,184,91,0.4)' : `${colors.primary}50`}`,
                  borderRadius: 'var(--radius)', padding: '7px 14px',
                  cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
                }}
              >
                <Icon name={shareCopied ? 'check' : 'link'} size={14} />
                {shareCopied ? 'Copied!' : 'Share'}
              </button>

              {timeline.events?.length > 0 && (
                <button
                  onClick={handleCarouselExport}
                  disabled={carouselExporting}
                  aria-label="Export as carousel slides"
                  title="Download a zip of 1080×1080 slides for Instagram, LinkedIn, Facebook, and X"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    background: `${colors.primary}22`,
                    color: colors.primary,
                    border: `1px solid ${colors.primary}50`,
                    borderRadius: 'var(--radius)', padding: '7px 14px',
                    cursor: carouselExporting ? 'default' : 'pointer',
                    fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
                    opacity: carouselExporting ? 0.6 : 1,
                  }}
                >
                  <Icon name="image" size={14} />
                  {carouselExporting ? 'Generating…' : 'Carousel'}
                </button>
              )}

              {onEdit && (
                <button
                  onClick={onEdit}
                  aria-label={`Edit ${timeline.title}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: colors.primary, color: getContrastColor(colors.primary),
                    border: 'none', borderRadius: 'var(--radius)',
                    padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}
                >
                  <Icon name="edit" size={14} /> Edit
                </button>
              )}

              {currentUser && timeline.userId !== currentUser.id && timeline.privacy === 'public' && (
                <button
                  onClick={handleFork}
                  disabled={forkLoading}
                  aria-label="Fork this timeline"
                  title="Create your own editable copy of this timeline"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    background: `${colors.primary}18`,
                    color: colors.primary,
                    border: `1px solid ${colors.primary}40`,
                    borderRadius: 'var(--radius)', padding: '7px 14px',
                    cursor: forkLoading ? 'default' : 'pointer', fontSize: 13, fontWeight: 500,
                    opacity: forkLoading ? 0.6 : 1, transition: 'all 0.2s',
                  }}
                >
                  <Icon name="copy" size={14} />
                  {forkLoading ? 'Forking…' : `Fork${timeline.forkCount ? ` (${timeline.forkCount})` : ''}`}
                </button>
              )}
            </>)}

            {/* Mobile hamburger — all actions + Notifications + Theme */}
            {isMobile && (
              <MobileMenu>
                {() => <>
                  <button
                    onClick={handleBookmark}
                    disabled={bookmarkLoading}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left', opacity: bookmarkLoading ? 0.6 : 1 }}
                  >
                    <Icon name="bookmark" size={15} fill={isBookmarked ? 'currentColor' : 'none'} color="currentColor" />
                    {isBookmarked ? `Saved${bookmarkCount > 0 ? ` (${bookmarkCount})` : ''}` : `Save${bookmarkCount > 0 ? ` (${bookmarkCount})` : ''}`}
                  </button>

                  <button
                    onClick={handleShare}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: shareCopied ? '#5cb85c' : 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <Icon name={shareCopied ? 'check' : 'link'} size={15} />
                    {shareCopied ? 'Copied!' : 'Share'}
                  </button>

                  {timeline.events?.length > 0 && (
                    <button
                      onClick={handleCarouselExport}
                      disabled={carouselExporting}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: carouselExporting ? 'default' : 'pointer', textAlign: 'left', opacity: carouselExporting ? 0.6 : 1 }}
                    >
                      <Icon name="image" size={15} />
                      {carouselExporting ? 'Generating…' : 'Carousel'}
                    </button>
                  )}

                  {onEdit && (
                    <button
                      onClick={onEdit}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <Icon name="edit" size={15} /> Edit
                    </button>
                  )}

                  {currentUser && timeline.userId !== currentUser.id && timeline.privacy === 'public' && (
                    <button
                      onClick={handleFork}
                      disabled={forkLoading}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: forkLoading ? 'default' : 'pointer', textAlign: 'left', opacity: forkLoading ? 0.6 : 1 }}
                    >
                      <Icon name="copy" size={15} />
                      {forkLoading ? 'Forking…' : `Fork${timeline.forkCount ? ` (${timeline.forkCount})` : ''}`}
                    </button>
                  )}

                  {currentUser?.id && onNotificationClick && (
                    <div style={{ padding: '4px 16px' }}>
                      <NotificationBell userId={currentUser.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />
                    </div>
                  )}

                  {onThemeToggle && (
                    <button
                      onClick={onThemeToggle}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
                      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                    </button>
                  )}
                </>}
              </MobileMenu>
            )}
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4vw, 36px)', color: colors.primary, marginBottom: 8 }}>
            {timeline.title}
          </h1>
          {minDate && (
            <p style={{ fontSize: 12, color: `${colors.primary}bb`, marginBottom: hasVisibleContent(timeline.description) ? 20 : 0 }}>
              <span className="sr-only">Date range: </span>
              {formatDate(minDate, minShowEra)} → {formatDate(maxDate, maxShowEra)}
            </p>
          )}
          {hasVisibleContent(timeline.description) && (
            <div
              dangerouslySetInnerHTML={{ __html: stripImgTags(timeline.description) }}
              className="viewer-banner-desc"
              style={{ fontSize: 14, color: `${colors.primary}cc`, lineHeight: 1.6, maxWidth: 680, margin: '0 auto' }}
            />
          )}
        </div>
      </header>

      {/* ── Content ── */}
      <main className="viewer-content page-main" style={{ padding: 32, maxWidth: 1200, margin: '0 auto' }}>

        <TimelineCanvas timeline={timeline} onEventClick={setSelectedEvent} />

        {/* Search result banner — shown when opened from a keyword search */}
        {searchHighlight && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            background: 'color-mix(in srgb, var(--accent) 10%, var(--surface))',
            border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
            borderRadius: 'var(--radius)', padding: '10px 16px', marginTop: 24,
          }}>
            <Icon name="search" size={14} />
            <span style={{ fontSize: 13, color: 'var(--text2)', flex: 1 }}>
              Showing results for <strong style={{ color: 'var(--accent)' }}>"{searchHighlight}"</strong>
              {highlightedEventIds.size > 0 && (
                <span style={{ color: 'var(--text3)', marginLeft: 8 }}>
                  · {highlightedEventIds.size} matching event{highlightedEventIds.size !== 1 ? 's' : ''}
                </span>
              )}
            </span>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => { setSearchHighlight(''); setHighlightedEventIds(new Set()); }}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Clear
            </button>
          </div>
        )}

        <section style={{ marginTop: 40 }} aria-label="All events">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 16, color: 'var(--text)' }}>
            All Events ({timeline.events.length})
            {highlightedEventIds.size > 0 && (
              <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--accent)', marginLeft: 10 }}>
                · {highlightedEventIds.size} matching your search
              </span>
            )}
          </h2>
          {timeline.events.length === 0 ? (
            <p style={{ color: 'var(--text3)', fontSize: 14 }}>No events have been added to this timeline yet.</p>
          ) : (
            <div className="event-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {sorted.map(ev => {
                const isMatch = highlightedEventIds.has(ev.id);
                return (
                  <button
                    key={ev.id}
                    onClick={() => setSelectedEvent(ev)}
                    aria-label={`${ev.title}, ${formatDate(ev.startDate, ev.startEra !== 'NONE')} to ${formatDate(ev.endDate, ev.endEra !== 'NONE')}${isMatch ? ' (matches search)' : ''}`}
                    style={{
                      textAlign: 'left',
                      background: isMatch
                        ? `color-mix(in srgb, var(--accent) 18%, ${colors.tertiary}cc)`
                        : `${colors.tertiary}cc`,
                      border: `1px solid ${isMatch ? 'var(--accent)' : `${colors.primary}30`}`,
                      borderRadius: 'var(--radius-lg)',
                      padding: '16px 20px', cursor: 'pointer', transition: 'all 0.2s',
                      outline: isMatch ? '2px solid color-mix(in srgb, var(--accent) 40%, transparent)' : 'none',
                      outlineOffset: 2,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = isMatch ? 'var(--accent)' : `${colors.primary}30`; e.currentTarget.style.transform = 'none'; }}
                  >
                    {isMatch && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                        <Icon name="search" size={10} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Match</span>
                      </div>
                    )}
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: getContrastColor(colors.tertiary), marginBottom: 6 }}>{ev.title}</h3>
                    <p style={{ fontSize: 11, color: getContrastColor(colors.tertiary), opacity: 0.7, marginBottom: ev.description ? 8 : 0 }} aria-hidden="true">
                      {formatDate(ev.startDate, ev.startEra !== 'NONE')}{ev.endDate && ev.endDate !== ev.startDate ? ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : ''}
                    </p>
                    {hasTextContent(ev.description) && (
                      <p style={{ fontSize: 13, color: getContrastColor(colors.tertiary), opacity: 0.82, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {stripHtml(ev.description)}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {collaborators.length > 0 && (
          <section style={{ marginTop: 40, borderTop: '1px solid var(--border)', paddingTop: 32 }} aria-label="Collaborators">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 16, color: 'var(--text)' }}>Contributors</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {collaborators.map(c => (
                <button
                  key={c.id}
                  onClick={() => onViewProfile?.(c)}
                  disabled={!onViewProfile}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '8px 14px',
                    cursor: onViewProfile ? 'pointer' : 'default',
                    textAlign: 'left', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => { if (onViewProfile) e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                  aria-label={`View ${c.name}'s profile`}
                >
                  {c.avatar
                    ? <img src={c.avatar} alt={c.name} style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>{c.name[0]}</div>
                  }
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.name}</p>
                    <p style={{ fontSize: 11, color: 'var(--text3)' }}>{c.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── Social panel ── */}
        {currentUser && social && (
          <section style={{ marginTop: 40, borderTop: '1px solid var(--border)', paddingTop: 32 }} aria-label="Likes and comments">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 20, color: 'var(--text)' }}>Discussion</h2>

            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              {/* Reaction button + picker */}
              {(() => {
                const likes        = social.likes || [];
                const userReaction = likes.find(l => l.userId === currentUser.id) || null;
                const isReacted    = !!userReaction;
                const counts       = likes.reduce((acc, l) => {
                  const t = l.reactionType || 'heart';
                  acc[t] = (acc[t] || 0) + 1;
                  return acc;
                }, {});
                return (
                  <div
                    style={{ position: 'relative' }}
                    onMouseEnter={() => { clearTimeout(reactionPickerTimer.current); setShowReactionPicker(true); }}
                    onMouseLeave={() => { reactionPickerTimer.current = setTimeout(() => setShowReactionPicker(false), 200); }}
                  >
                    {showReactionPicker && (
                      <div style={{
                        position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, zIndex: 60,
                        display: 'flex', gap: 4, padding: '6px 8px',
                        background: 'var(--surface)', border: '1px solid var(--border2)',
                        borderRadius: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                      }}>
                        {REACTIONS.map(r => (
                          <button
                            key={r.type}
                            type="button"
                            onClick={() => handleReact(r.type)}
                            aria-label={r.label}
                            title={r.label}
                            style={{
                              background: (userReaction?.reactionType || 'heart') === r.type && isReacted ? 'var(--bg3)' : 'none',
                              border: 'none', borderRadius: 8, padding: '4px 6px',
                              fontSize: 20, cursor: 'pointer',
                              transform: (userReaction?.reactionType || 'heart') === r.type && isReacted ? 'scale(1.25)' : 'scale(1)',
                              transition: 'transform 0.1s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.35)'}
                            onMouseLeave={e => {
                              const active = (userReaction?.reactionType || 'heart') === r.type && isReacted;
                              e.currentTarget.style.transform = active ? 'scale(1.25)' : 'scale(1)';
                            }}
                          >
                            {r.emoji}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => handleReact(userReaction?.reactionType || 'heart')}
                      aria-label={isReacted ? `Remove ${userReaction.reactionType} reaction` : 'React'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s',
                        background: isReacted ? 'rgba(224,92,92,0.12)' : 'var(--surface)',
                        border: `1px solid ${isReacted ? 'rgba(224,92,92,0.5)' : 'var(--border2)'}`,
                        color: isReacted ? '#e05c5c' : 'var(--text2)',
                      }}
                    >
                      {isReacted
                        ? <span style={{ fontSize: 16, lineHeight: 1 }}>{REACTIONS.find(r => r.type === (userReaction.reactionType || 'heart'))?.emoji ?? '❤️'}</span>
                        : <Icon name="heart" size={16} fill="none" color="currentColor" />
                      }
                      {likes.length === 0 ? 'React' : (
                        <span style={{ display: 'flex', gap: 6 }}>
                          {REACTIONS.filter(r => counts[r.type]).map(r => (
                            <span key={r.type} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <span style={{ fontSize: 14 }}>{r.emoji}</span>
                              <span>{counts[r.type]}</span>
                            </span>
                          ))}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })()}

              <button
                onClick={() => setShowComments(v => !v)}
                aria-expanded={showComments}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s',
                  background: showComments ? 'var(--accent)18' : 'var(--surface)',
                  border: `1px solid ${showComments ? 'var(--accent)50' : 'var(--border2)'}`,
                  color: showComments ? 'var(--accent)' : 'var(--text2)',
                }}
              >
                <Icon name="message" size={16} />
                {(() => {
                  const total = (social.comments || []).length + (social.comments || []).reduce((s, c) => s + (c.replies?.length || 0), 0);
                  return total > 0 ? `${total} Comment${total !== 1 ? 's' : ''}` : 'Comments';
                })()}
              </button>
            </div>

            {showComments && (
              <div style={{ maxWidth: 680 }}>
                {/* New comment input */}
                {!currentUser ? (
                  <div style={{ marginBottom: 24, padding: '10px 14px', borderRadius: 8, background: 'var(--surface2)', textAlign: 'center', fontSize: 13, color: 'var(--text2)' }}>
                    <button className="btn-link" style={{ fontWeight: 600 }} onClick={() => onAuthRequired?.('comment')}>Sign in</button> to leave a comment
                  </div>
                ) : (
                <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'flex-start' }}>
                  {renderAvatar(currentUser, 34)}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <textarea
                      ref={commentTextareaRef}
                      value={commentText}
                      onChange={e => {
                        setCommentText(e.target.value.slice(0, 500));
                        handleMentionInput(e.target.value, 'comment');
                      }}
                      placeholder="Write a comment… (use @ to mention someone)"
                      rows={2}
                      style={{ resize: 'none', width: '100%', marginBottom: 8, fontSize: 13, boxSizing: 'border-box' }}
                      onKeyDown={handleCommentKeyDown}
                      onBlur={() => setTimeout(() => setMentionActive(null), 150)}
                    />
                    {/* @mention dropdown */}
                    {mentionActive === 'comment' && mentionResults.length > 0 && (
                      <div className="mention-dropdown">
                        {mentionResults.filter(Boolean).map((u, i) => (
                          <button
                            key={u.id}
                            className={`mention-option${i === mentionIndex ? ' selected' : ''}`}
                            onMouseDown={e => { e.preventDefault(); insertMention(u, 'comment'); }}
                          >
                            {u.avatar
                              ? <img src={u.avatar} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                              : <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>{(u.name || '?')[0]}</div>
                            }
                            <span>{u.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {mentionActive === 'comment' && mentionResults.length === 0 && mentionQuery.length > 0 && (
                      <div className="mention-dropdown">
                        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text3)' }}>No users found</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button className="btn-primary btn-sm" onClick={handleComment} disabled={!commentText.trim()} style={{ opacity: commentText.trim() ? 1 : 0.5 }}>
                        Comment
                      </button>
                    </div>
                  </div>
                </div>
                )}

                {(social.comments || []).filter(c => !blockedIds.has(c.userId)).length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>No comments yet. Be the first!</p>
                ) : (
                  <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {(social.comments || []).filter(c => !blockedIds.has(c.userId)).map(c => (
                      <li key={c.id} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', gap: 12 }}>
                          {renderAvatar({ name: c.userName, avatar: c.userAvatar }, 34)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{c.userName}</span>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{timeAgo(c.timestamp)}</span>
                                  {currentUser && c.userId !== currentUser.id && !reportedIds.has(c.id) && (
                                    <button onClick={() => setReportTarget({ type: 'comment', id: c.id })} aria-label="Report comment"
                                      title="Report" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                    ><Icon name="flag" size={12} /></button>
                                  )}
                                  {currentUser && c.userId !== currentUser.id && !blockedIds.has(c.userId) && (
                                    <button onClick={() => confirmBlock(c.userId, c.userName)} aria-label="Block user"
                                      title="Block user" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                    ><Icon name="ban" size={12} /></button>
                                  )}
                                  {currentUser && c.userId === currentUser.id && (
                                    <button onClick={() => handleDeleteComment(c.id)} aria-label="Delete comment"
                                      style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                    ><Icon name="trash" size={12} /></button>
                                  )}
                                </div>
                              </div>
                              <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, margin: 0 }}>{renderMentionText(c.text)}</p>
                            </div>
                            {/* Comment footer: like + reply */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {renderLikeButton(c.id, false)}
                              <button
                                onClick={() => { if (!currentUser) { onAuthRequired?.('comment'); return; } setReplyingTo(replyingTo === c.id ? null : c.id); setReplyText(''); }}
                                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 12, padding: '2px 4px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              >
                                <Icon name="reply" size={12} />
                                {c.replies?.length > 0 ? `${c.replies.length} repl${c.replies.length !== 1 ? 'ies' : 'y'}` : 'Reply'}
                              </button>
                            </div>
                            {replyingTo === c.id && (
                              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start' }}>
                                {renderAvatar(currentUser, 26)}
                                <div style={{ flex: 1, position: 'relative' }}>
                                  <textarea ref={replyTextareaRef} value={replyText} onChange={e => {
                                    setReplyText(e.target.value.slice(0, 500));
                                    handleMentionInput(e.target.value, 'reply');
                                  }}
                                    placeholder={`Reply to ${c.userName}… (use @ to mention)`} rows={2} autoFocus
                                    style={{ resize: 'none', width: '100%', marginBottom: 6, fontSize: 12, boxSizing: 'border-box' }}
                                    onKeyDown={handleReplyKeyDown}
                                    onBlur={() => setTimeout(() => setMentionActive(null), 150)}
                                  />
                                  {mentionActive === 'reply' && mentionResults.length > 0 && (
                                    <div className="mention-dropdown">
                                      {mentionResults.filter(Boolean).map((u, i) => (
                                        <button key={u.id} className={`mention-option${i === mentionIndex ? ' selected' : ''}`}
                                          onMouseDown={e => { e.preventDefault(); insertMention(u, 'reply'); }}>
                                          {u.avatar
                                            ? <img src={u.avatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                                            : <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent)' }}>{(u.name || '?')[0]}</div>
                                          }
                                          <span>{u.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                    <button className="btn-ghost btn-sm" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</button>
                                    <button className="btn-primary btn-sm" onClick={() => handleReply(c.id)} disabled={!replyText.trim()} style={{ opacity: replyText.trim() ? 1 : 0.5 }}>Reply</button>
                                  </div>
                                </div>
                              </div>
                            )}
                            {c.replies?.filter(r => !blockedIds.has(r.userId)).length > 0 && (
                              <ol style={{ listStyle: 'none', padding: '8px 0 0 0', margin: 0, borderLeft: '2px solid var(--border)', paddingLeft: 12 }}>
                                {c.replies.filter(r => !blockedIds.has(r.userId)).map(r => (
                                  <li key={r.id} style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
                                    {renderAvatar({ name: r.userName, avatar: r.userAvatar }, 26)}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                                          <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>{r.userName}</span>
                                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{timeAgo(r.timestamp)}</span>
                                            {currentUser && r.userId !== currentUser.id && !reportedIds.has(r.id) && (
                                              <button onClick={() => setReportTarget({ type: 'reply', id: r.id })} aria-label="Report reply"
                                                title="Report" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                              ><Icon name="flag" size={11} /></button>
                                            )}
                                            {currentUser && r.userId !== currentUser.id && !blockedIds.has(r.userId) && (
                                              <button onClick={() => confirmBlock(r.userId, r.userName)} aria-label="Block user"
                                                title="Block user" style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                              ><Icon name="ban" size={11} /></button>
                                            )}
                                            {currentUser && r.userId === currentUser.id && (
                                              <button onClick={() => handleDeleteReply(c.id, r.id)} aria-label="Delete reply"
                                                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                              ><Icon name="trash" size={11} /></button>
                                            )}
                                          </div>
                                        </div>
                                        <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, margin: 0 }}>{renderMentionText(r.text)}</p>
                                      </div>
                                      <div style={{ marginTop: 2 }}>
                                        {renderLikeButton(r.id, true)}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── Recommendations ── */}
        {recsLoaded && recommendations.length > 0 && (
          <section style={{ marginTop: 40, borderTop: '1px solid var(--border)', paddingTop: 32 }} aria-label="Recommended timelines">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 20, color: 'var(--text)' }}>
              You might also like
            </h2>
            <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'thin' }}>
              {recommendations.map(({ tl: rec, author: recAuthor }) => {
                const recAccent = rec.colors?.primary || rec.colors?.accent || 'var(--accent)';
                const handleRecClick = () => {
                  if (onViewTimeline) onViewTimeline(rec, recAuthor);
                  else if (onViewProfile) onViewProfile(recAuthor);
                };
                return (
                  <button
                    key={rec.id}
                    type="button"
                    onClick={handleRecClick}
                    style={{
                      flexShrink: 0, width: 220, textAlign: 'left',
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-xl)', overflow: 'hidden',
                      cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = recAccent; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                    aria-label={`View timeline: ${rec.title} by ${recAuthor.name}`}
                  >
                    <div style={{
                      height: 60, background: rec.bgImage
                        ? `url(${rec.bgImage}) center/cover no-repeat`
                        : recAccent,
                    }} />
                    <div style={{ padding: '10px 14px' }}>
                      <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', lineHeight: 1.35, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {rec.title}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)' }}>by {recAuthor.name}</p>
                      {rec.category && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--bg2)', color: 'var(--text3)', display: 'inline-block', marginTop: 6 }}>
                          {rec.category}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* ── Modals ── */}
      <ReportModal
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        contentType={reportTarget?.type}
        contentId={reportTarget?.id}
        onReported={() => { setReportedIds(p => new Set([...p, reportTarget?.id])); setReportTarget(null); }}
        currentUser={currentUser}
        onBlock={blockConfirm ? null : (userId, userName) => confirmBlock(userId, userName)}
      />

      {/* Block confirmation dialog */}
      <Modal open={!!blockConfirm} onClose={() => setBlockConfirm(null)} title="Block User" width={400}>
        {blockConfirm && (
          <div>
            <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              Block <strong style={{ color: 'var(--text)' }}>{blockConfirm.userName}</strong>?
              They will not be able to comment on your timelines or follow you.
              <br /><span style={{ color: 'var(--text3)', fontSize: 12 }}>They will not be notified that you blocked them.</span>
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-ghost btn-sm" onClick={() => setBlockConfirm(null)}>Cancel</button>
              <button className="btn-danger" onClick={executeBlock}>Block</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!selectedEvent} onClose={() => setSelectedEvent(null)} title={selectedEvent?.title || ''} width={520}>
        {selectedEvent && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)' }}>
                {formatDate(selectedEvent.startDate, selectedEvent.startEra !== 'NONE')}
              </span>
              {selectedEvent.endDate && selectedEvent.endDate !== selectedEvent.startDate && (<>
                <span aria-hidden="true" style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>→</span>
                <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)' }}>
                  {formatDate(selectedEvent.endDate, selectedEvent.endEra !== 'NONE')}
                </span>
                <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--text3)' }}>
                  {daysBetween(selectedEvent.startDate, selectedEvent.endDate)} days
                </span>
              </>)}
            </div>

            {selectedEvent.photo && (
              <img src={selectedEvent.photo} alt={`Photo for ${selectedEvent.title}`}
                style={{ display: 'block', width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 'var(--radius)', marginBottom: 16, border: '1px solid var(--border)' }}
              />
            )}

            {selectedEvent.videoUrl && (
              <YouTubeEmbed embedUrl={selectedEvent.videoUrl} title={selectedEvent.title} />
            )}

            {hasVisibleContent(selectedEvent.description) ? (
              <div dangerouslySetInnerHTML={{ __html: selectedEvent.description }}
                style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7, marginBottom: selectedEvent.sources?.length ? 20 : 0 }}
              />
            ) : (
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7, marginBottom: selectedEvent.sources?.length ? 20 : 0 }}>
                No description provided.
              </p>
            )}

            {selectedEvent.sources?.length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  Sources ({selectedEvent.sources.length})
                </p>
                {selectedEvent.sources.map((src, i) => (
                  <div key={src.id || i} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8 }}>
                    {src.label && <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginBottom: src.text ? 6 : 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{src.label}</p>}
                    {src.text  && <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{src.text}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TimelineViewer;
